import { AppFilters } from '@/lib/types';
import JurisdictionRules from '@/constants/JurisdictionRules';
import MarketApprovalKeywords from '@/constants/MarketApprovalKeywords';
import { pdfjsLib } from '@/lib/pdfjs';
import type { TextItem } from 'pdfjs-dist/types/src/display/api';

type FilterGame = AppFilters['games'][number];

export type PdfSearchGame = FilterGame;

const isPdfFile = (file: File) =>
  file.type === 'application/pdf' ||
  file.name.toLowerCase().endsWith('.pdf');

export { isPdfFile };

const escapeRegExp = (str: string) =>
  str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const extractTextFromPdf = async (file: File): Promise<string> => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer })
    .promise;
  let fullText = '';

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const pageText = textContent.items
      .map((item) => ('str' in item ? item.str : ''))
      .join(' ');
    fullText += pageText + '\n';
  }

  return fullText;
};

export type PdfTextItem = Pick<
  TextItem,
  'str' | 'transform' | 'width' | 'height'
>;

const baselineOf = (item: PdfTextItem) => item.transform[5];
const startOf = (item: PdfTextItem) => item.transform[4];
const fontSizeOf = (item: PdfTextItem) =>
  Math.abs(item.transform[3]) || item.height || 1;

/**
 * Text of a page, one line per visual line. pdf.js may split a single
 * word into several items, so items on a line are glued together unless
 * there is a visible gap between them.
 */
export const pageLinesText = (items: PdfTextItem[]): string => {
  const sortedItems = items
    .filter((item) => item.str.trim())
    .sort(
      (a, b) =>
        baselineOf(b) - baselineOf(a) || startOf(a) - startOf(b),
    );

  const lines: PdfTextItem[][] = [];
  sortedItems.forEach((item) => {
    const line = lines[lines.length - 1];
    const sameLine =
      line &&
      Math.abs(baselineOf(line[0]) - baselineOf(item)) <=
        fontSizeOf(item) / 2;
    if (sameLine) line.push(item);
    else lines.push([item]);
  });

  return lines
    .map((line) =>
      line
        .sort((a, b) => startOf(a) - startOf(b))
        .reduce((text, item, index) => {
          if (index === 0) return item.str;
          const prev = line[index - 1];
          const gap = startOf(item) - (startOf(prev) + prev.width);
          const separator = gap > fontSizeOf(item) * 0.25 ? ' ' : '';
          return text + separator + item.str;
        }, ''),
    )
    .join('\n');
};

// How each testing lab writes a certificate code; an approval quotes the
// same code. Letter case doesn't matter. A code is matched only as a
// whole: it may not continue with more letters or digits on either side.
// Keep in sync with the backend's `utils/document_codes.py`.
const LAB_CODE_PATTERNS = [
  // eCOGRA: e2616547RPLESPM, e2517960RPLPENM_R02, e2515165RPLWVAM_GR. An
  // underscore suffix is part of the code, so each of e2517960RPLPENM,
  // e2517960RPLPENM_R01 and e2517960RPLPENM_TSR is a different document.
  String.raw`(?<![a-z0-9])[a-z]?\d{5,}RPL[a-z0-9]*(?:_[a-z0-9]+)*(?![a-z0-9_])`,
  // Gaming Associates: NSY-NL-230206-01-GC-R1, RBP-CO-240508-01-GC-R2.
  String.raw`(?<![a-z0-9])[a-z]{2,5}-[a-z]{2,3}-\d{6}-\d{2}-GC-[a-z]{1,2}\d+(?![a-z0-9])`,
  // BMM: NET.RUB-OL.1001.01.01 (stops before a trailing ".pdf" but not
  // inside the number).
  String.raw`(?<![a-z0-9.])[a-z]{2,5}\.RUB-OL(?:\.\d+)+(?![a-z0-9]|\.\d)`,
];

const LAB_CODE_REGEX = new RegExp(
  LAB_CODE_PATTERNS.map((pattern) => `(?:${pattern})`).join('|'),
  'gi',
);

// The backend accepts at most this many codes per lookup.
const MAX_CERTIFICATE_CODES = 200;

/**
 * Every distinct certificate code in the text (an approval may quote
 * several), upper-case as the backend stores them, in order of
 * appearance.
 */
export const findCertificateCodes = (text: string): string[] =>
  [
    ...new Set(
      Array.from(text.matchAll(LAB_CODE_REGEX), ([code]) =>
        code.toUpperCase(),
      ),
    ),
  ].slice(0, MAX_CERTIFICATE_CODES);

/** The first certificate code in the text, upper-case. */
export const findCertificateCode = (
  text: string,
): string | undefined => findCertificateCodes(text)[0];

// A keyword as whole words, in any case, with any run of whitespace
// (a PDF may wrap a phrase onto the next line) between its words.
const keywordRegExp = (keyword: string) =>
  new RegExp(
    `(?<![\\p{L}\\p{N}_])${keyword
      .normalize('NFC')
      .trim()
      .split(/\s+/)
      .map(escapeRegExp)
      .join('\\s+')}(?![\\p{L}\\p{N}_])`,
    'iu',
  );

const MARKET_APPROVAL_KEYWORD_REGEXPS = MarketApprovalKeywords.map(
  ({ keyword, marketName }) => ({
    regExp: keywordRegExp(keyword),
    marketName,
  }),
);

/**
 * Market of the regulator keyword (see `MarketApprovalKeywords`) that
 * appears first in an approval's text. Matched as whole words in any
 * case, so `wvlottery` counts in `www.wvlottery.com`, but not inside a
 * longer word.
 */
export const findKeywordMarketName = (
  text: string,
): string | undefined => {
  const normalized = text.normalize('NFC');
  let found: { index: number; marketName: string } | undefined;
  for (const {
    regExp,
    marketName,
  } of MARKET_APPROVAL_KEYWORD_REGEXPS) {
    const index = normalized.search(regExp);
    if (index !== -1 && (!found || index < found.index)) {
      found = { index, marketName };
    }
  }
  return found?.marketName;
};

const withoutExtension = (fileName: string) => {
  const dot = fileName.lastIndexOf('.');
  return dot > 0 ? fileName.slice(0, dot) : fileName;
};

// File names separate their parts with underscores as often as with
// spaces, so both delimit the code — except before a revision suffix
// (`_R02`), which stays part of the code.
const fileNameWords = (fileName: string) =>
  withoutExtension(fileName).replace(/_(?!R\d+(?:[_\s]|$))/g, ' ');

// The code as a whole token of the name, underscores included. A
// different revision (`_R01`) after it makes it another document.
const fileNameCarriesCode = (fileName: string, code: string) =>
  new RegExp(
    `(?<![a-z0-9])${escapeRegExp(code)}(?![a-z0-9]|_R\\d)`,
    'i',
  ).test(fileName);

/**
 * The certificate code in a file name, upper-case. Splitting on
 * underscores cuts a suffix like `_GR` off a code, so the content's code
 * wins whenever the name carries it whole.
 */
export const findFileNameCertificateCode = (
  fileName: string,
  contentCode?: string,
): string | undefined =>
  contentCode && fileNameCarriesCode(fileName, contentCode)
    ? contentCode.toUpperCase()
    : findCertificateCode(fileNameWords(fileName));

/**
 * A certificate's code: the one in its file name, or, when the name has
 * none, the one found in its content.
 */
export const resolveCertificateCode = (
  fileName: string,
  contentCode: string | undefined,
): string | undefined =>
  findFileNameCertificateCode(fileName, contentCode) ?? contentCode;

export type CertificateCodeMismatch = {
  fileNameCode: string;
  documentCode: string;
};

/**
 * The two codes when a certificate's file name and content both carry
 * one and they differ; otherwise nothing, as there is nothing to compare.
 */
export const findCertificateCodeMismatch = (
  fileName: string,
  contentCode: string | undefined,
): CertificateCodeMismatch | undefined => {
  const fileNameCode = findFileNameCertificateCode(
    fileName,
    contentCode,
  );
  if (!fileNameCode || !contentCode) return undefined;
  return fileNameCode === contentCode.toUpperCase()
    ? undefined
    : { fileNameCode, documentCode: contentCode.toUpperCase() };
};

type PdfContent = {
  fullText: string;
  /** Page text with lines rebuilt from item positions. */
  linesText: string;
};

const extractPdfContent = async (file: File): Promise<PdfContent> => {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer })
    .promise;
  let fullText = '';
  let linesText = '';

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    const items = textContent.items.filter(
      (item): item is TextItem => 'str' in item,
    );
    fullText += items.map((item) => item.str).join(' ') + '\n';
    linesText += pageLinesText(items) + '\n';
  }

  return { fullText, linesText };
};

// Whitespace-insensitive key: PDF text may break a term across lines or
// split it into separately extracted items joined with spaces.
const normalizeTerm = (str: string) =>
  str.replace(/\s+/g, '').toLowerCase();

// Matches the term with optional whitespace (incl. newlines) between any
// two of its characters.
const toWhitespaceTolerantPattern = (term: string) =>
  [...term.replace(/\s+/g, '')].map(escapeRegExp).join('\\s*');

const findTermsInText = (
  searchTerms: string[],
  fullText: string,
): string[] => {
  const uniqueTerms = [...new Set(searchTerms)].filter(
    (term) => normalizeTerm(term).length > 0,
  );
  if (uniqueTerms.length === 0) return [];

  const regexPattern = uniqueTerms
    .map(toWhitespaceTolerantPattern)
    .join('|');
  const searchRegex = new RegExp(regexPattern, 'gi');
  const matches = fullText.match(searchRegex) || [];
  const normalizedFound = new Set(matches.map(normalizeTerm));

  return uniqueTerms.filter((term) =>
    normalizedFound.has(normalizeTerm(term)),
  );
};

const findGamesForGameGroups = (
  gameGroups: string[],
  games: FilterGame[],
): FilterGame[] => {
  const lowerGameGroups = gameGroups.map((group) =>
    group.toLowerCase(),
  );

  return games.filter((game) =>
    lowerGameGroups.some((group) =>
      game.game_name.toLowerCase().includes(group),
    ),
  );
};

const extractRtpFromGameName = (gameName: string): number | null => {
  const numbers = gameName.match(/\d+/g);
  if (!numbers?.length) return null;
  return parseInt(numbers[numbers.length - 1], 10);
};

const findRtpsInText = (
  rtps: number[],
  fullText: string,
): number[] => {
  const uniqueRtps = [...new Set(rtps)];
  if (uniqueRtps.length === 0) return [];

  const rtpPatterns = uniqueRtps.map(
    (rtp) => `${escapeRegExp(String(rtp))}(?:\\.\\d+)?%?`,
  );
  const searchRegex = new RegExp(rtpPatterns.join('|'), 'gi');
  const matches = fullText.match(searchRegex) || [];
  const foundRtps = new Set<number>();

  matches.forEach((match) => {
    const rtpMatch = match.match(/\d+/);
    if (rtpMatch) {
      foundRtps.add(parseInt(rtpMatch[0], 10));
    }
  });

  return uniqueRtps.filter((rtp) => foundRtps.has(rtp));
};

export const searchPDF = async (
  gameGroups: string[],
  games: FilterGame[],
  file: File,
): Promise<PdfSearchGame[]> => {
  if (!file || !isPdfFile(file)) return [];

  console.log(`Processing PDF: ${file.name}`);

  const startTime = performance.now();

  try {
    const fullText = await extractTextFromPdf(file);

    console.log('Text extracted. Scanning game groups...');

    const foundGameGroups = findTermsInText(gameGroups, fullText);

    console.log(
      `Found ${foundGameGroups.length} game group(s) in ${file.name}:`,
      foundGameGroups,
    );

    if (foundGameGroups.length === 0) {
      const duration = (
        (performance.now() - startTime) /
        1000
      ).toFixed(2);
      console.log(`Completed in ${duration} seconds.`);
      return [];
    }

    const matchedGames = findGamesForGameGroups(
      foundGameGroups,
      games,
    );

    console.log(
      `Games matching game groups in ${file.name}:`,
      matchedGames,
    );

    const rtps = [
      ...new Set(
        matchedGames
          .map((game) => extractRtpFromGameName(game.game_name))
          .filter((rtp): rtp is number => rtp !== null),
      ),
    ];

    console.log(`RTPs derived from games in ${file.name}:`, rtps);

    const foundRtpsInDocument = findRtpsInText(rtps, fullText);

    console.log(
      `RTPs found in ${file.name} document:`,
      foundRtpsInDocument,
    );

    const confirmedGames = matchedGames.filter((game) => {
      const rtp = extractRtpFromGameName(game.game_name);
      return rtp !== null && foundRtpsInDocument.includes(rtp);
    });

    console.log(`Confirmed games in ${file.name}:`, confirmedGames);

    const duration = ((performance.now() - startTime) / 1000).toFixed(
      2,
    );
    console.log(`Completed in ${duration} seconds.`);

    return confirmedGames;
  } catch (error) {
    console.error(error);
    console.log(`Error processing PDF: ${file.name}`);
    return [];
  }
};

export type DetectedFileMetadata = {
  marketName?: string;
  language?: string;
  documentTag?: string;
};

const DOCUMENT_TAG_RULES = [
  {
    filenameKeyword: '_AR',
    documentTag: 'Assessment Report',
  },
  {
    filenameKeyword: '_CC',
    documentTag: 'Examination Report',
  },
  {
    filenameKeyword: '_TSR',
    documentTag: 'Testing Summary Report',
  },
];

// `EN` as a standalone token in any case, with an underscore on at
// least one side: `en_` counts, but the letters inside
// `Sample_Garden` and the Spanish word " en " do not.
const ENGLISH_MARKER = /(?:^|[^a-z0-9])en_|_en(?![a-z0-9])/i;

const isEnglishFileName = (fileName: string) =>
  ENGLISH_MARKER.test(fileName);

/**
 * Language for a file stored under `marketName`, by the same rule
 * detection uses: English when the name marks it so, otherwise the
 * jurisdiction's local language, falling back to English.
 */
export const languageForMarket = (
  marketName: string,
  fileName: string,
): string => {
  if (isEnglishFileName(fileName)) return 'en';
  const rule = JurisdictionRules.find(
    (r) => r.marketName?.toLowerCase() === marketName.toLowerCase(),
  );
  return rule?.language ?? 'en';
};

export const detectFileJurisdiction = (
  fileName: string,
): DetectedFileMetadata => {
  const rule = JurisdictionRules.find((r) =>
    fileName.includes(r.filenameKeyword),
  );

  if (!rule) return {};

  const documentTagRule = DOCUMENT_TAG_RULES.find((r) =>
    fileName.includes(r.filenameKeyword),
  );
  const documentTag = documentTagRule?.documentTag;

  return {
    marketName: rule.marketName,
    language: isEnglishFileName(fileName)
      ? 'en'
      : (rule.language ?? 'en'),
    ...(documentTag ? { documentTag } : {}),
  };
};

export type GameIdentifierKind =
  'studio_game_id' | 'jar_file_name' | 'sha1';

// Order matters: the first identifier kind found in the file decides its
// games and the search stops there.
const GAME_IDENTIFIER_CASCADE: GameIdentifierKind[] = [
  'studio_game_id',
  'jar_file_name',
  'sha1',
];

export type CertificateDetectionResult = {
  games: PdfSearchGame[];
  /** Identifier kind the games were resolved from; absent when none found. */
  identifier?: GameIdentifierKind;
  /** First certificate code found in the document; absent when none. */
  certificateCode?: string;
};

const findGamesByIdentifier = (
  games: FilterGame[],
  kind: GameIdentifierKind,
  fullText: string,
): FilterGame[] => {
  const identifierOf = (game: FilterGame) => game[kind]?.trim() || '';
  // Longest first so a shorter id never shadows a longer one sharing its prefix.
  const identifiers = games
    .map(identifierOf)
    .filter((id) => !!id)
    .sort((a, b) => b.length - a.length);

  const found = new Set(
    findTermsInText(identifiers, fullText).map((id) =>
      id.toLowerCase(),
    ),
  );
  if (found.size === 0) return [];

  return games.filter((game) => {
    const id = identifierOf(game);
    return !!id && found.has(id.toLowerCase());
  });
};

/**
 * Reads a certificate's content once and resolves:
 * - the game + RTP variants it covers: Game Studio ID first, then JAR
 *   file name, then SHA-1. A file carrying several identifiers of the
 *   winning kind resolves to all of their games.
 * - its certificate code, the first one anywhere in the document.
 */
export const detectCertificateDetails = async (
  games: FilterGame[],
  file: File,
): Promise<CertificateDetectionResult> => {
  if (!file || !isPdfFile(file)) return { games: [] };

  try {
    const { fullText, linesText } = await extractPdfContent(file);
    const certificateCode = findCertificateCode(linesText);

    for (const kind of GAME_IDENTIFIER_CASCADE) {
      const matched = findGamesByIdentifier(games, kind, fullText);
      if (matched.length) {
        return { games: matched, identifier: kind, certificateCode };
      }
    }

    return { games: [], certificateCode };
  } catch (error) {
    console.error(error);
    console.log(`Error processing PDF: ${file.name}`);
    return { games: [] };
  }
};

export type ApprovalContentResult = {
  /** Certificate codes quoted in the approval, for the backend lookup. */
  codes: string[];
  /** Market of a jurisdiction keyword in the text, used when no code matches. */
  keywordMarketName?: string;
};

/** Reads an approval's content once for its certificate codes and keyword. */
export const detectApprovalContent = async (
  file: File,
): Promise<ApprovalContentResult> => {
  if (!file || !isPdfFile(file)) return { codes: [] };

  try {
    const { linesText } = await extractPdfContent(file);
    return {
      codes: findCertificateCodes(linesText),
      keywordMarketName: findKeywordMarketName(linesText),
    };
  } catch (error) {
    console.error(error);
    console.log(`Error processing PDF: ${file.name}`);
    return { codes: [] };
  }
};
